import { useDispatch, useSelector } from "react-redux";
import LikertEvaluation from "./LikertEvaluation";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import { Alert, Button, Col, Container, Form, Row, Spinner } from "react-bootstrap";
import { editBossComment, editBossValue, editEmployeeComment, editEmployeeValue, getBossValue, getEmployeeValue } from "../../../redux/evaluacion_desempenio/EvaluationValue/actions";
import { useEffect, useState } from "react";
import { canEdit, canView, EVALUATION_SPECIFIC_VIEWS, QUARTERS, ROLES, STATUSES } from "../../../pages/evaluacion_desempenio/utils/Constants";

/**
 * Componente para mostrar el formulario de evaluación por valores.
 * Permite la autoevaluación y la evaluación del jefe, según permisos.
 */
const ValueEvaluationForm = ({
    idUser,               // ID del usuario actual
    evaluationRole,     // Rol del usuario en la evaluación (empleado o jefe)
    evaluationEtapa,    // Etapa actual de la evaluación
    setValuesEvaluationReady // Función para indicar que la evaluación de valores está lista    
}) => {
    const dispatch = useDispatch();
    const { idEvaluacion } = useParams();

    const [employeeComment, setEmployeeComment] = useState("");
    const [bossComment, setBossComment] = useState("");

    // Obtiene los estados y datos de valores desde Redux
    const {
        loadingEmployeeValue,
        saveLoadingEmployeeValue,
        employeeValues,
        employeeValueError,

        loadingBossValue,
        saveLoadingBossValue,
        bossValues,
        bossValueError
    } = useSelector((store) => store.evaluationValue);

    const {
        loadingById: loadingEvaluationMonitor,
        evaluationMonitor,
        evaluationMonitorError
    } = useSelector((store) => store.evaluationMonitor);

    // Determinar si el usuario ya ha enviado la evaluación en el 3Q
    const evaluationDone = STATUSES.PENDIENTE_EVALUACION &&
        ((evaluationMonitor?.data_evaluacion_objetivos?.jefe_completo_evaluacion && idUser === evaluationMonitor?.evaluador) ||
            (evaluationMonitor?.data_evaluacion_objetivos?.empleado_completo_evaluacion && idUser === evaluationMonitor?.evaluado));

    //Determinar si el jefe puede ver la evaluación de competencias del empleado
    const canBossSeeEmployeeEvaluation = (evaluationMonitor?.data_evaluacion_objetivos?.empleado_completo_evaluacion && idUser === evaluationMonitor?.evaluador) || idUser === evaluationMonitor?.evaluado;

    useEffect(() => {
        console.log(employeeValues, bossValues, evaluationRole)
        if (evaluationRole === ROLES.EMPLOYEE && employeeValues?.data?.length > 0) {
            // Indica al padre que la evaluación de valores del empleado está lista
            let allAnswered = employeeValues?.data?.every(item => item.metrica !== null);
            let employeeCommentCompleted = employeeValues?.data?.[0]?.empleado_evaluacion_data?.comentario_general_empleado !== "" && employeeValues?.data?.[0]?.empleado_evaluacion_data?.comentario_general_empleado !== null;
            setValuesEvaluationReady(allAnswered && employeeCommentCompleted);
            console.log("Employee values evaluation ready:", allAnswered && employeeCommentCompleted);
        }
        if (evaluationRole === ROLES.BOSS && bossValues?.data?.length > 0) {
            // Indica al padre que la evaluación de valores del jefe está lista
            let allAnswered = bossValues?.data?.every(item => item.metrica !== null);
            let bossCommentCompleted = bossValues?.data?.[0]?.empleado_evaluacion_data?.comentario_general_jefe !== "" && bossValues?.data?.[0]?.empleado_evaluacion_data?.comentario_general_jefe !== null;
            setValuesEvaluationReady(allAnswered && bossCommentCompleted);
            console.log("Boss values evaluation ready:", allAnswered && bossCommentCompleted);
        }
    }, [employeeValues, bossValues]);

    /**
     * Handler para actualizar la métrica de un valor (empleado o jefe).
     * @param {Object} data - Valor evaluado
     * @param {number} newMetric - Nueva métrica seleccionada
     * @param {number} index - Índice en el array
     */
    const handleValueEvaluationSubmit = async (data, newMetric, index) => {
        let req = {
            evaluacion_empleado: Number(idEvaluacion),
            valor: data.valor,
            metrica: newMetric
        }
        // Determina si la edición corresponde al empleado o al jefe
        if (evaluationRole === ROLES.EMPLOYEE) {
            await dispatch(editEmployeeValue(req, data.id, index))
            await dispatch(getEmployeeValue({ evaluacion_empleado: idEvaluacion }));
        } else if (evaluationRole === ROLES.BOSS) {
            await dispatch(editBossValue(req, data.id, index))
            await dispatch(getBossValue({ evaluacion_empleado: idEvaluacion }));
        }
    }

    const handleSaveComment = async (data) => {
        if (evaluationRole === ROLES.EMPLOYEE) {
            if (employeeComment === "") {
                alert("El comentario no puede estar vacío.");
                return;
            }
            await dispatch(editEmployeeComment({
                comentario_general_empleado: employeeComment,
                preguntas_completas: true
            }, employeeValues?.data?.[0]?.id))
            await dispatch(getEmployeeValue({ evaluacion_empleado: idEvaluacion }));
        } else if (evaluationRole === ROLES.BOSS) {
            if (bossComment === "") {
                alert("El comentario no puede estar vacío.");
                return;
            }
            await dispatch(editBossComment({
                comentario_general_jefe: bossComment,
                preguntas_completas: true
            }, bossValues?.data?.[0]?.id))
            await dispatch(getBossValue({ evaluacion_empleado: idEvaluacion }));
        }
    };

    let allQuestionsAnsweredEmployee = employeeValues?.data?.length > 0 && employeeValues?.data?.every(item => item.metrica !== null);
    let allQuestionsAnsweredBoss = bossValues?.data?.length > 0 && bossValues?.data?.every(item => item.metrica !== null);


    return (
        <div>
            <h3 className="my-5">Evaluación por Valores</h3>

            {/* Sección de autoevaluación */}
            {canView(evaluationRole, [QUARTERS.THIRD], evaluationEtapa, { condition: canBossSeeEmployeeEvaluation, quarter: QUARTERS.THIRD }) && (
                <div style={{ position: 'relative' }}>
                    {/* Loading overlay para autoevaluación */}
                    {(loadingEmployeeValue || saveLoadingEmployeeValue) && (
                        <div 
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                zIndex: 1000,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: '200px'
                            }}
                        >
                            <div className="text-center">
                                <Spinner animation="border" size="sm" className="mr-2" />
                                <span>Cargando...</span>
                            </div>
                        </div>
                    )}

                    <div className="d-flex flex-row">
                        <h5>Autoevaluación</h5>
                    </div>
                    
                    {/* Alerta de error */}
                    <Alert variant="danger" show={employeeValueError}>
                        {employeeValueError}
                    </Alert>
                    
                    {/* Componente de evaluación tipo Likert */}
                    <LikertEvaluation
                        data={employeeValues || []}
                        onSubmit={handleValueEvaluationSubmit}
                        readOnly={(loadingEmployeeValue || saveLoadingEmployeeValue) || canEdit(evaluationRole, [QUARTERS.THIRD], evaluationEtapa, { condition: !evaluationDone, quarter: QUARTERS.THIRD }) === false || evaluationRole !== ROLES.EMPLOYEE}
                        type={"Valores"}
                    />
                </div>
            )}

            {/* Sección de evaluación del jefe */}
            {canView(evaluationRole, [QUARTERS.THIRD], evaluationEtapa, null, EVALUATION_SPECIFIC_VIEWS.BOSS_VALUE_EVALUATION) && (
                <div style={{ position: 'relative' }}>
                    {/* Loading overlay para evaluación del jefe */}
                    {(loadingBossValue || saveLoadingBossValue) && (
                        <div 
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                zIndex: 1000,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: '200px'
                            }}
                        >
                            <div className="text-center">
                                <Spinner animation="border" size="sm" className="mr-2" />
                                <span>Cargando...</span>
                            </div>
                        </div>
                    )}

                    <div className="d-flex flex-row">
                        <h5>Evaluación del Jefe</h5>
                    </div>
                    
                    {/* Alerta de error */}
                    <Alert variant="danger" show={bossValueError}>
                        {bossValueError}
                    </Alert>
                    
                    {/* Componente de evaluación tipo Likert */}
                    <LikertEvaluation
                        data={bossValues || []}
                        onSubmit={handleValueEvaluationSubmit}
                        readOnly={(loadingBossValue || saveLoadingBossValue) || canEdit(evaluationRole, [QUARTERS.THIRD], evaluationEtapa, { condition: !evaluationDone, quarter: QUARTERS.THIRD }) === false}
                        type={"Valores"}
                    />
                </div>
            )}

            <div className="my-3">
                <Container fluid>
                    <Row>
                        {canView(evaluationRole, [QUARTERS.THIRD], evaluationEtapa, { condition: canBossSeeEmployeeEvaluation, quarter: QUARTERS.THIRD }) && employeeValues?.data?.length > 0 && (
                            <Col xs={12} md={6} className="mb-3">
                                <Row>
                                    <Col className="text-white bg-dark py-3">
                                        Resultado Autoevaluación
                                    </Col>
                                    <Col className="text-right py-3 bg-light">
                                        {employeeValues?.data?.[0]?.empleado_evaluacion_data?.nota_valores_empleado_final?.metrica_total || "-"}
                                    </Col>
                                </Row>
                            </Col>
                        )}
                        {canView(evaluationRole, [QUARTERS.THIRD], evaluationEtapa, null, EVALUATION_SPECIFIC_VIEWS.BOSS_EVALUATION_RESULTS) && bossValues?.data?.length > 0 && (
                            <Col xs={12} md={6} className="mb-3">
                                <Row>
                                    <Col className="text-white bg-dark py-3">
                                        Resultado Evaluación Jefe
                                    </Col>
                                    <Col className="text-right py-3 bg-light">
                                        {bossValues?.data?.[0]?.empleado_evaluacion_data?.nota_valores_jefe_final?.metrica_total || "-"}
                                    </Col>
                                </Row>
                            </Col>
                        )}
                    </Row>
                </Container>

                {canView(evaluationRole, [QUARTERS.THIRD], evaluationEtapa, { condition: canBossSeeEmployeeEvaluation, quarter: QUARTERS.THIRD }) && (
                    <div className="my-4 p-3">
                        <Row>
                            <Col className="border p-3 text-center" style={{ fontWeight: "bold" }} xs={9}>CALIFICACIÓN GENERAL DEL EMPLEADO</Col>
                            <Col className="border text-center p-3" xs={3}> {evaluationMonitor?.data_evaluacion_objetivos?.clasificacion_nota_empleado} </Col>
                        </Row>
                        <Row>
                            <Col className="border p-3" style={{ fontWeight: "bold" }} xs={3}>Comentario General Empleado: (250 caracteres incluyendo espacios)</Col>
                            <Col className="bg-light border p-3" xs={9}>
                                <Form.Control
                                    as="textarea"
                                    maxLength={250}
                                    disabled={(loadingEmployeeValue || saveLoadingEmployeeValue) || (!canEdit(evaluationRole, [QUARTERS.THIRD], evaluationEtapa, { condition: !evaluationDone, quarter: QUARTERS.THIRD }) && evaluationRole === ROLES.EMPLOYEE) || evaluationRole !== ROLES.EMPLOYEE}
                                    value={employeeComment || employeeValues?.data?.[0]?.empleado_evaluacion_data?.comentario_general_empleado || ""}
                                    onChange={(e) => setEmployeeComment(e.target.value)}
                                />
                            </Col>
                        </Row>
                    </div>
                )}

                {canView(evaluationRole, [QUARTERS.THIRD], evaluationEtapa, null, EVALUATION_SPECIFIC_VIEWS.BOSS_EVALUATION_RESULTS) && (
                    <div className="my-4 p-3">
                        <Row>
                            <Col className="border p-3 text-center" style={{ fontWeight: "bold" }} xs={9}>CALIFICACIÓN GENERAL DEL JEFE</Col>
                            <Col className="border text-center p-3" xs={3}> {evaluationMonitor?.data_evaluacion_objetivos?.clasificacion_nota_jefe} </Col>
                        </Row>
                        <Row>
                            <Col className="border p-3" style={{ fontWeight: "bold" }} xs={3}>Comentario General Jefe: (250 caracteres incluyendo espacios)</Col>
                            <Col className="bg-light border p-3" xs={9}>
                                <Form.Control
                                    as="textarea"
                                    maxLength={250}
                                    disabled={(loadingBossValue || saveLoadingBossValue) || !canEdit(evaluationRole, [QUARTERS.THIRD], evaluationEtapa, { condition: !evaluationDone, quarter: QUARTERS.THIRD })}
                                    value={bossComment || bossValues?.data?.[0]?.empleado_evaluacion_data?.comentario_general_jefe || ""}
                                    onChange={(e) => setBossComment(e.target.value)}
                                />
                            </Col>
                        </Row>
                    </div>
                )}

                {/*Botón para guardar el comentario*/}
                {((allQuestionsAnsweredBoss && evaluationRole === ROLES.BOSS) ||
                    (allQuestionsAnsweredEmployee && evaluationRole === ROLES.EMPLOYEE)) &&
                    canEdit(evaluationRole, [QUARTERS.THIRD], evaluationEtapa, { condition: !evaluationDone, quarter: QUARTERS.THIRD }) && (
                    <div className="d-flex justify-content-end">
                        <Button 
                            variant="primary" 
                            className="rounded-pill" 
                            disabled={loadingEmployeeValue || saveLoadingEmployeeValue || loadingBossValue || saveLoadingBossValue}
                            onClick={handleSaveComment}
                        >
                            Guardar Comentario
                        </Button>
                    </div>
                )}
            </div>
        </div>
    )
}

export default ValueEvaluationForm;